import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { FlowStateService, FlowStep } from '../shared/services/flow-state/flow-state.service';

const START_STEP: FlowStep = 'application';

/**
 * Guard factory that accepts multiple valid prerequisite paths
 * 
 * Each path is an array of required steps in order (AND logic within path)
 *   Example: ['login', 'application'] -> has completed login AND application
 * 
 * Multiple paths use OR logic (any path can satisfy the requirement)
 *   Example: loginPath: [...], applicationPath: [...] -> has completed login path OR application path
 * 
 * These logic flows can be combined to evaluate both paths and steps in those paths
 *   Example: flowGuard('transaction', [['login', 'verify-otp'], ['application', 'decide-application']])
 * 
 * This means user can access transaction if they completed EITHER:
 * - login AND verify-otp
 *          OR
 * - application AND decide-application
 */
export function flowGuard(step: FlowStep, requiredPaths: FlowStep[][]): CanActivateFn {
  return () => {
    const flowService = inject(FlowStateService);
    const router = inject(Router);

    // Check if ANY of the prerequisite paths is fully completed
    const hasValidPath = requiredPaths.some(path => 
      path.every(reqStep => flowService.hasCompletedStep(reqStep))
    );

    // If we have a valid path up to this step, the current step is valid
    if (hasValidPath) {
      return true;
    }

    // Otherwise, find the first incomplete step from the most-completed path as fallback
    let furthestStep = -1;
    let bestPathIndex = 0; // Default to first path
    requiredPaths.forEach((path, index) => {
      const distance = path.findIndex(step => !flowService.hasCompletedStep(step));

      if (furthestStep > distance) {
        furthestStep = distance;
        bestPathIndex = index;
      }
    });

    const firstIncomplete = requiredPaths[bestPathIndex].find(s => !flowService.hasCompletedStep(s));
    const target = firstIncomplete || START_STEP;
    router.navigate([target]);
    return false;
  };
}

/**
 * Simple guard for steps with only one prerequisite path
 */
export function simpleFlowGuard(step: FlowStep, requiredSteps: FlowStep[]): CanActivateFn {
  return flowGuard(step, [requiredSteps]);
}